import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { KEY_DATAPROVIDEAGREESEQ } from 'tools/WebTool';
import ROUTE_PATH from 'routes/config';
import { BusinessManagerMsg, TopNoticeWrap } from './style';

import useIdentification from 'hooks/useIdentification';
import api from '../../utils/api';
import { requestApi } from '../../utils/request';
import { useApp } from '../../contexts/AppContext';
import { useUI } from '../../contexts/UIContext';

const TopNotice = ({
  onClickClose = () => {},
  bannerMessage = '',
  bannerData = {},
}) => {
  const { auth } = useApp();
  const { contextLoading, setModalData } = useUI();
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const [businessManagerHash, setBusinessManagerHash] = useState('');
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    businessRegistrationNumber: 0,
    invitationPeriodExpiredYn: '',
  });

  const NO_BANNER_PATH = useMemo(() => {
    // 데이터 제공 동의, 데이터 연동 관리, 데이터 연동, 약관 페이지에서는 주요 액션을 취하니까 안내 배너를 보여주지 않는다.
    return [
      ROUTE_PATH.deepLink.agree,
      ROUTE_PATH.mypage.dataLinkageManage,
      ROUTE_PATH.mypage.dataLinkageDataInterlockRequest,
      ROUTE_PATH.mypage.dataLinkageDataProviderRequest,
      ROUTE_PATH.policyDefault,
    ];
  }, []);

  const onClickText = () => {
    if (bannerData?.code === 'MDAA') {
      navigate(`${ROUTE_PATH.deepLink.agree}?type=add-mall`);
      return;
    }

    const messageActions = {
      '데이터 제공 동의': () => {
        if (bannerData?.dataProvideAgreeSeq) {
          localStorage.setItem(
            KEY_DATAPROVIDEAGREESEQ,
            bannerData.dataProvideAgreeSeq
          );
          navigate(ROUTE_PATH.deepLink.agree);
        }
      },
      '데이터 연동': () => navigate(ROUTE_PATH.mypage.dataLinkageManage),
      '직원 초대': () =>
        setModalData({
          contents: businessManagerMsg,
          cancelBtnName: '닫기',
          confirmBtnName: '초대 수락하기',
          onClickConfirmBtn: onClickIdentification,
        }),
    };

    Object.entries(messageActions).forEach(([key, action]) => {
      if (bannerMessage.includes(key)) action();
    });
  };

  const businessManagerMsg = useMemo(
    () => (
      <BusinessManagerMsg>
        <p className="title">
          <span>{businessInfo?.businessName}</span>
          <br />
          사업자 직원으로 초대되었습니다.
          <br />
          수락하시겠습니까?
        </p>
        <p className="desc">
          2일 이내 수락이 가능하며, 2일 경과시 수락이 불가합니다.
          <br />
          사업자와 연결이 끊어진 회원은 사이트의 사용이 제한됩니다.
          <br />
          초대 수락시 본인인증이 필요합니다.
          <FeatherIcon
            icon="alert-circle"
            stroke="#555"
            size={15}
            strokeWidth={2}
          />
        </p>
      </BusinessManagerMsg>
    ),
    [businessInfo?.businessName]
  );

  const inviteMember = useCallback(
    ({ memberCertificationSeq }) => {
      const data = {
        memberCertificationSeq,
        businessManagerHash: businessManagerHash,
      };
      api({
        url: `/v1/au/manager/invite-accept`,
        method: 'put',
        headers: { 'content-type': 'application/json' },
        data: data,
      })
        .then(res => {
          const { data } = res;
          if (data.success) {
            setModalData({
              contents: data.message,
              onClickCloseBtn: () => window.location.reload(),
            });
          } else {
            setModalData({
              contents: data.message,
            });
          }
        })
        .catch(err => {
          setModalData({
            contents: err?.response?.data?.message,
          });
        });
    },
    [businessManagerHash]
  );

  const { onClickIdentification } = useIdentification({
    identificationSuccessCallback: useCallback(
      memberCertificationSeq => {
        inviteMember({ memberCertificationSeq });
      },
      [inviteMember]
    ),
    identificationExceptCallback: useCallback(err => {
      console.error(err);
      setModalData({ contents: err?.response?.data?.message });
    }, []),
  });

  useEffect(() => {
    const isInviteBanner =
      auth.bannerMessage.includes('직원 초대') &&
      !NO_BANNER_PATH.includes(pathname) &&
      pathname === '/' &&
      !contextLoading;

    if (!isInviteBanner) return;

    setBusinessManagerHash(auth.bannerData.businessManagerHash);

    setModalData({
      contents: businessManagerMsg,
      cancelBtnName: '닫기',
      confirmBtnName: '초대 수락하기',
      onClickConfirmBtn: () => onClickIdentification(),
    });
  }, [
    auth.bannerMessage,
    auth.bannerData.businessManagerHash,
    pathname,
    contextLoading,
    businessManagerMsg,
    onClickIdentification,
  ]);

  const fetchInviteExpired = async () => {
    try {
      const res = await requestApi({
        method: 'GET',
        url: `/v1/na/manager/invite-expired/${businessManagerHash}`,
      });

      if (res.success) {
        setBusinessInfo(res.data.result);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!businessManagerHash) return;

    (async () => {
      await fetchInviteExpired();
    })();
  }, [businessManagerHash]);

  return (
    <>
      <TopNoticeWrap className="topNotice">
        <p onClick={onClickText}>{bannerMessage}</p>
        <button type="button" onClick={onClickClose}>
          <FeatherIcon icon="x" stroke="#fff" size={20} strokeWidth={2.2} />
        </button>
      </TopNoticeWrap>
    </>
  );
};

export default TopNotice;
